// This component should ultimately replace app/javascript/shared/TileComponent/TileComponent.js
// They are two components for now because the original TileComponent is being reused heavily
// The redesign should move logic for reusing the TileComponent onto this component as we see fit.

import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import themeGet from '@styled-system/theme-get';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import LoadingComponent from '../LoadingComponent';
import { imgOnError } from '../helpers';
import RibbonTagComponent from '../RibbonTagComponent';
import TileStatsComponent from '../TileStatsComponent/TileStatsComponent';
import { RESTRICTED_SITE_ADMIN } from '../../lib/utils/constants/UserTypes';

const NewTileComponent = ({
  copyButtonDisplay,
  viewButtonDisplay,
  copyPath,
  copyTile,
  date,
  embedVideo,
  headline,
  id,
  loading,
  shadowOverlayButtons,
  thumbnail,
  tileStats,
  tileThumblinkClass,
  tileThumbnailClass,
  tileThumblinkOnClick,
  user,
  shadowOverlay,
  audienceColor,
  ribbonTagName,
  ribbonTagColor,
  popdownMenu,
}) => {
  const { t } = useTranslation('shared');
  const { userType } = useSelector(state => state.currentUser);

  return (
    <TileContainer data-tile-container-id={id} disabled={loading}>
      <div
        className={`tile_thumbnail ${tileThumbnailClass}`}
        id={`single-tile-${id}`}
        style={{ maxWidth: '257px' }}
      >
        <TileWrapper>
          <div
            onClick={tileThumblinkOnClick}
            className={tileThumblinkClass}
            data-tile-id={id}
          >
            <TileThumbnailImage $hasVideo={!!embedVideo}>
              {audienceColor && <CampaignTag background={audienceColor} />}
              <img src={thumbnail} onError={imgOnError} />
              {ribbonTagName && (
                <RibbonTagComponent
                  ribbonTagName={ribbonTagName}
                  ribbonTagColor={ribbonTagColor}
                  height="28"
                  bottom="154px"
                />
              )}
            </TileThumbnailImage>

            <TextWrapper>
              {date && (
                <DateField>
                  {t('tileComponent.verified')}
                  {date}
                </DateField>
              )}
              <div style={{ paddingTop: '8px' }}>{headline}</div>
            </TextWrapper>

            {loading && (
              <LoadingOverlay>
                <LoadingComponent />
              </LoadingOverlay>
            )}
            <ShadowOverlay className="shadow_overlay" overlay={shadowOverlay}>
              {shadowOverlayButtons && (
                <ul className="tile_buttons">{shadowOverlayButtons}</ul>
              )}
            </ShadowOverlay>
            {popdownMenu && (
              <div className="tile-popdown-menu">{popdownMenu}</div>
            )}
            <div className="tile_overlay"></div>
          </div>
        </TileWrapper>
        {copyButtonDisplay && !(user.isGuestUser || user.isEndUser) && (
          <CopyButton
            onClick={() => copyTile({ id, copyPath })}
            id={id}
            data-tile-id={id}
            data-section="Explore"
          >
            {t('tileComponent.copy')}
          </CopyButton>
        )}
        {viewButtonDisplay && (
          <CopyButton onClick={tileThumblinkOnClick}>
            {t('tileComponent.view')}
          </CopyButton>
        )}
      </div>
      {userType !== RESTRICTED_SITE_ADMIN && tileStats && (
        <TileStatsComponent tileStats={tileStats} tileId={id} />
      )}
    </TileContainer>
  );
};

NewTileComponent.propTypes = {
  id: PropTypes.string.isRequired,
  thumbnail: PropTypes.string.isRequired,
  headline: PropTypes.string.isRequired,
  embedVideo: PropTypes.string,
  copyPath: PropTypes.string,
  copyTile: PropTypes.func,
  user: PropTypes.shape({
    isGuestUser: PropTypes.bool,
    isEndUser: PropTypes.bool,
  }),
  date: PropTypes.string,
  copyButtonDisplay: PropTypes.bool,
  viewButtonDisplay: PropTypes.bool,
  shadowOverlay: PropTypes.bool,
  tileContainerClass: PropTypes.string,
  tileThumbnailClass: PropTypes.string,
  tileThumblinkClass: PropTypes.string,
  shadowOverlayButtons: PropTypes.array,
  popdownMenu: PropTypes.element,
  tileThumblinkOnClick: PropTypes.func,
  loading: PropTypes.bool,
  ignored: PropTypes.bool,
  calendarClass: PropTypes.string,
  audienceColor: PropTypes.string,
  draggable: PropTypes.bool,
  tileStats: PropTypes.object,
  ribbonTagName: PropTypes.string,
  ribbonTagColor: PropTypes.string,
  moveTileBackToPlan: PropTypes.func,
};

export default NewTileComponent;

const DateField = styled.div`
  font-size: 9px;
  font-style: normal;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: 0.1px;
  padding-top: 8px;
`;

const TextWrapper = styled.div`
  text-align: left;
  font-family: 'Montserrat', sans-serif;
  font-weight: 500;
  color: ${themeGet('colors.black.100')};
  height: 130px;
  padding: 0px 16px;
  background-color: ${themeGet('colors.white')};
`;

const CopyButton = styled.div`
  background-color: ${themeGet('colors.primary.100')};
  width: 100%;
  font-size: 12px;
  height: 24px;
  border-radius: 0px 0px 8px 8px;
  color: ${themeGet('colors.white')};
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  cursor: pointer;
  &:hover {
    background: ${themeGet('colors.primary.80')};
  }
  &:active {
    background: ${themeGet('colors.primary.120')};
  }
`;

const TileContainer = styled.div`
  ${({ disabled }) => disabled && ' pointerEvents: none;'}
  text-align: center;
  padding: 0px;
  width: 257px;
  display: block;

  &.placeholder_container {
    margin-bottom: 17px;
  }

  @media only screen and (max-width: 410px) {
    width: 100%;
    padding-left: 2px;
    padding-right: 2px;
  }
`;

const TileThumbnailImage = styled.div`
  height: 12em;
  margin: 0 auto;
  width: 100%;
  background-color: ${props =>
    props.$hasVideo ? 'black' : themeGet('colors.black.10')};
  border: none;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  border-radius: 8px 8px 0px 0px;
  position: relative;
  img {
    object-fit: ${props => (props.$hasVideo ? 'contain' : 'cover')};
    image-rendering: pixelated;
    flex-shrink: 0;
    min-width: 100%;
    min-height: 100%;
  }
`;

const LoadingOverlay = styled.div`
  display: block;
  width: 100%;
  height: 100%;
  position: absolute;
  bottom: 0;
  left: 0;
  background: rgba(255, 255, 255, 0.92);
`;

const TileWrapper = styled.div`
  border-radius: 8px 8px 0 0;
  &:hover,
  &.active_menu {
    .shadow_overlay,
    .tile_buttons {
      opacity: 1;
    }
  }
  border: solid 1px ${themeGet('colors.black.10')};
  border-bottom: 0;
`;

const ShadowOverlay = styled.div`
  background-color: ${props => !props.overlay && 'transparent !important'};
  &:after {
    content: ${props => !props.overlay && 'none !important'};
  }
`;

const CampaignTag = styled.div`
  background-color: ${props => props.background};
  height: 8px;
  border-radius: 6px 6px 0px 0px;
  width: calc(100% - 2px);
  height: 8px;
  position: absolute;
  top: 0px;
`;
